import { useEffect } from "react";

import { ReviewRating } from "common/types";
import { parseNumber } from "common/utils/numbers";
import { useSearchState } from "common/router/hooks";
import errorImages from "common/constants/errorImages";

import Results from "client/common/components/results/Results";
import ReviewCreateModal from "client/reviews/components/ReviewCreateModal";
import { ModalStatus } from "client/common/types";
import Page from "client/common/components/layouts/Page";
import Subheader from "client/common/components/layouts/Subheader";
import Body from "client/common/components/layouts/Body";
import { Paths, urls } from "client/routes";

import { useOrdersQuery } from "../hooks";
import OrderModificationsModal from "./OrderModificationsModal";
import ListOrder from "./ListOrder";
import { useOrderCancelationDrawer } from "../stackables";

import styles from "../styles/OrdersPage.module.less";

const initialOrdersSearch = {
  reviewCreateModalIsVisible: ModalStatus.Closed,
  modificationsModalIsVisible: ModalStatus.Closed,
  cancelationModalIsVisible: ModalStatus.Closed,
};

const Orders = () => {
  const { open: openCancelationDrawer } = useOrderCancelationDrawer();
  const { data: orders = [], isLoading, error: ordersError } = useOrdersQuery();

  const [params, setParams] = useSearchState<Paths["orders"]["list"]>(initialOrdersSearch);

  // we should eventually move all modals to hooks like this.
  // see the subscription detail page for a full example
  useEffect(() => {
    if (params.cancelationModalIsVisible === ModalStatus.Open) {
      openCancelationDrawer({
        orderId: parseNumber(params.orderId, 0),
        onClose: () => setParams(initialOrdersSearch),
      });
    }
    // we only want to open the drawer once, when the params change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.cancelationModalIsVisible]);

  if (isLoading) {
    return <Results.Loading description="Loading orders" />;
  }

  if (ordersError) {
    return <Results.FetchError entityName="orders" error={ordersError} isPlural={true} />;
  }

  if (!orders.length) {
    return (
      <Results.None
        image={errorImages.EmptyOrders}
        header="You haven't placed any orders yet"
        dest={urls.products.search()}
        button="Search Products"
      />
    );
  }

  const openOrderModifications = (orderId: number) => {
    setParams({
      modificationsModalIsVisible: ModalStatus.Open,
      orderId,
    });
  };
  const openOrderCancelation = (orderId: number) => {
    setParams({
      cancelationModalIsVisible: ModalStatus.Open,
      orderId,
    });
  };

  return (
    <div className={styles.orders}>
      {orders.map((order) => (
        <ListOrder
          key={order.id}
          order={order}
          onOpenReview={({ orderItem, rating }) =>
            setParams({
              reviewCreateModalIsVisible: ModalStatus.Open,
              productLineId: orderItem.item.product.product_line.id,
              initialRating: rating,
            })
          }
          onOpenModifications={() => openOrderModifications(order.id)}
          onOpenCancelation={() => openOrderCancelation(order.id)}
        />
      ))}
      <ReviewCreateModal
        visible={params.reviewCreateModalIsVisible === ModalStatus.Open}
        onClose={() => setParams(initialOrdersSearch)}
        initialRating={parseNumber(params.initialRating) as ReviewRating}
        productLineId={parseNumber(params.productLineId)}
      />
      <OrderModificationsModal
        visible={params.modificationsModalIsVisible === ModalStatus.Open}
        onClose={() => setParams(initialOrdersSearch)}
        orderId={parseNumber(params.orderId, 0)}
      />
    </div>
  );
};

const OrdersPage = () => {
  return (
    <Page>
      <Subheader
        title="Order History"
        description="Check the status of recent orders and quickly reorder items"
      />
      <Body>
        <Orders />
      </Body>
    </Page>
  );
};

export default OrdersPage;
